import React, { useEffect, useRef, useState } from "react";
import { Loader2, Mail, Send } from "lucide-react";
import { adminApi, formatApiError } from "../../lib/api";

export default function EmailsTab() {
  const [emailPack, setEmailPack] = useState("all");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailRecipients, setEmailRecipients] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [emailError, setEmailError] = useState("");
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showLinkHelper, setShowLinkHelper] = useState(false);
  const [linkText, setLinkText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const bodyRef = useRef(null);

  const loadEmailPreview = async (pack) => {
    try { setEmailRecipients(await adminApi.emailPreview(pack)); }
    catch { setEmailRecipients(null); }
  };

  useEffect(() => { loadEmailPreview(emailPack); }, []);

  const onPackChange = (pack) => {
    setEmailPack(pack); setEmailResult(null); setEmailError(""); loadEmailPreview(pack);
  };

  const onSendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) { setEmailError("El asunto y el cuerpo son obligatorios."); return; }
    if (!window.confirm(`Enviar email a ${emailRecipients?.count} destinatario(s)?`)) return;
    setEmailSending(true); setEmailError(""); setEmailResult(null); setTestResult(null);
    try {
      const res = await adminApi.sendEmail(emailPack, emailSubject.trim(), emailBody.trim());
      setEmailResult(res); setEmailSubject(""); setEmailBody("");
    } catch (e) { setEmailError(formatApiError(e?.response?.data?.detail) || "Error al enviar"); }
    finally { setEmailSending(false); }
  };

  const onSendTest = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) { setEmailError("Rellena asunto y cuerpo primero."); return; }
    setTestSending(true); setEmailError(""); setTestResult(null); setEmailResult(null);
    try {
      const res = await adminApi.sendTestEmail(emailPack, emailSubject.trim(), emailBody.trim());
      setTestResult(res.sent_to);
    } catch (e) { setEmailError(formatApiError(e?.response?.data?.detail) || "Error al enviar"); }
    finally { setTestSending(false); }
  };

  const insertLink = () => {
    if (!linkText.trim() || !linkUrl.trim()) return;
    const syntax = `[${linkText.trim()}](${linkUrl.trim()})`;
    const ta = bodyRef.current;
    if (ta) {
      const s = ta.selectionStart, en = ta.selectionEnd;
      setEmailBody(emailBody.substring(0, s) + syntax + emailBody.substring(en));
      setTimeout(() => { ta.focus(); ta.selectionStart = ta.selectionEnd = s + syntax.length; }, 0);
    } else { setEmailBody((v) => v + syntax); }
    setLinkText(""); setLinkUrl(""); setShowLinkHelper(false);
  };

  const PACKS = [
    { value: "all", label: "Todas las ofertas" },
    { value: "1", label: "Oferta 1 · 44,90€" },
    { value: "2", label: "Oferta 2 · 46,90€" },
    { value: "3", label: "Oferta 3 · 46,90€" },
  ];

  return (
    <div className="p-6 lg:p-8" data-testid="emails-tab">
      <div className="mb-7">
        <h1 className="text-xl font-black tracking-wider">Emails a Suscriptores</h1>
        <p className="text-xs text-white/40 mt-0.5">Envía mensajes personalizados filtrados por pack</p>
      </div>

      <div className="border border-white/10 bg-[#0a0a0a] p-6 space-y-6 max-w-3xl" data-testid="email-section">
        {/* Pack selector */}
        <div>
          <label className="block text-[10px] tracking-[0.22em] uppercase font-bold text-white/55 mb-3">Destinatarios</label>
          <div className="flex flex-wrap gap-2">
            {PACKS.map((opt) => (
              <button key={opt.value} type="button" data-testid={`email-pack-${opt.value}`} onClick={() => onPackChange(opt.value)}
                className={`px-4 py-2 text-xs font-bold tracking-[0.18em] uppercase border transition-colors ${emailPack === opt.value ? "bg-[#FFCC00] text-black border-[#FFCC00]" : "bg-transparent text-white/60 border-white/12 hover:border-[#FFCC00] hover:text-[#FFCC00]"}`}>
                {opt.label}
              </button>
            ))}
          </div>
          {emailRecipients !== null && (
            <p className="mt-2 text-xs text-white/45">
              <span className="text-[#FFCC00] font-bold">{emailRecipients.count}</span> destinatario(s)
              {emailRecipients.excluded > 0 && <span className="ml-1 text-white/35"> · {emailRecipients.excluded} inscritos excluidos</span>}
            </p>
          )}
        </div>

        {/* Subject */}
        <div>
          <label className="block text-[10px] tracking-[0.22em] uppercase font-bold text-white/55 mb-2">Asunto</label>
          <input type="text" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="Ej: ¡XPRO FITNESS abre en septiembre!" data-testid="email-subject"
            className="w-full bg-black border border-white/12 text-white placeholder:text-white/20 px-4 py-3 text-sm focus:outline-none focus:border-[#FFCC00] transition-colors" />
        </div>

        {/* Body */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-[10px] tracking-[0.22em] uppercase font-bold text-white/55">Cuerpo del mensaje</label>
            <button type="button" onClick={() => setShowLinkHelper((v) => !v)} data-testid="email-insert-link-btn"
              className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase font-bold text-[#FFCC00] hover:text-white transition-colors">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
              Insertar enlace
            </button>
          </div>
          {showLinkHelper && (
            <div className="mb-3 p-4 border border-[#FFCC00]/25 bg-[#FFCC00]/5 space-y-3" data-testid="link-helper">
              <p className="text-[10px] text-white/45 tracking-wider uppercase font-bold">Insertar enlace clicable</p>
              <div className="flex gap-2">
                <input type="text" value={linkText} onChange={(e) => setLinkText(e.target.value)} placeholder="Texto visible" data-testid="link-text-input"
                  className="flex-1 bg-black border border-white/12 text-white placeholder:text-white/20 px-3 py-2 text-sm focus:outline-none focus:border-[#FFCC00]" />
                <input type="url" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://..." data-testid="link-url-input"
                  className="flex-1 bg-black border border-white/12 text-white placeholder:text-white/20 px-3 py-2 text-sm focus:outline-none focus:border-[#FFCC00]" />
                <button type="button" onClick={insertLink} disabled={!linkText.trim() || !linkUrl.trim()} data-testid="link-insert-confirm"
                  className="px-4 py-2 bg-[#FFCC00] text-black font-bold text-xs tracking-wider uppercase hover:bg-white transition-colors disabled:opacity-40">
                  Insertar
                </button>
              </div>
            </div>
          )}
          <textarea ref={bodyRef} value={emailBody} onChange={(e) => setEmailBody(e.target.value)}
            placeholder="Escribe aquí el mensaje para tus suscriptores..." rows={6} data-testid="email-body"
            className="w-full bg-black border border-white/12 text-white placeholder:text-white/20 px-4 py-3 text-sm focus:outline-none focus:border-[#FFCC00] transition-colors resize-none" />
          <p className="text-[10px] text-white/25 mt-1">Usa <code className="text-white/40">[Texto](URL)</code> para enlaces clicables.</p>
        </div>

        {emailError && <div className="p-3 bg-red-500/10 border border-red-500/30 text-red-300 text-sm" data-testid="email-error">{emailError}</div>}
        {testResult && <div className="p-3 bg-blue-500/10 border border-blue-500/30 text-blue-300 text-sm" data-testid="email-test-result">Email de prueba enviado a <strong>{testResult}</strong>.</div>}
        {emailResult && (
          <div className="p-3 bg-green-500/10 border border-green-500/30 text-green-300 text-sm" data-testid="email-result">
            <strong>{emailResult.sent}</strong> email(s) enviado(s).{emailResult.failed > 0 && <span className="ml-2 text-red-300">{emailResult.failed} fallido(s).</span>}
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-end gap-3">
          <button type="button" onClick={onSendTest} disabled={testSending || !emailSubject.trim() || !emailBody.trim()} data-testid="email-test-btn"
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border border-white/15 text-white/70 font-bold text-[11px] tracking-[0.2em] uppercase hover:border-[#FFCC00] hover:text-[#FFCC00] transition-colors disabled:opacity-40">
            {testSending ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />}
            {testSending ? "Enviando..." : "Enviar prueba a mi email"}
          </button>
          <button type="button" onClick={onSendEmail} disabled={emailSending || !emailSubject.trim() || !emailBody.trim()} data-testid="email-send-btn"
            className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-[#FFCC00] text-black font-bold text-[11px] tracking-[0.2em] uppercase hover:bg-white transition-colors disabled:opacity-40">
            {emailSending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            {emailSending ? "Enviando..." : `Enviar a ${emailRecipients?.count ?? "—"} suscriptores`}
          </button>
        </div>
      </div>
    </div>
  );
}
